import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useSEO } from '../../utils/seo';

export default function MasAcademyPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.masdeuna.org';
  
  useSEO({
    title: 'MAS Academy - Free Professional Development Workshops for Latina Leaders',
    description: 'MAS Academy offers free professional development workshops for Latina leaders including Leadership Fundamentals, Executive Presence, Negotiation Mastery, Strategic Networking, Financial Literacy, and more. Self-development should not have a cost of entry.',
    keywords: 'Latina professional development, leadership workshops, free courses, executive presence, negotiation skills, financial literacy, MasDeUna',
    canonical: `${siteUrl}/mas-academy`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'EducationalOrganization',
      name: 'MAS Academy',
      url: `${siteUrl}/mas-academy`,
      description: 'Professional development workshops designed specifically for Latina leaders',
      provider: {
        '@type': 'Organization',
        name: 'MasDeUna',
        url: siteUrl
      },
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD',
        availability: 'https://schema.org/InStock'
      },
      courseMode: 'online',
      educationalLevel: 'Professional Development'
    }
  });

  const courses = [
    {
      title: 'Leadership Fundamentals',
      description: 'Build essential leadership skills including communication, decision-making, and team management.',
      icon: 'ri-user-star-line',
      color: 'bg-[#E6007E]'
    },
    {
      title: 'Executive Presence',
      description: 'Develop the confidence and gravitas needed to command respect in any room.',
      icon: 'ri-award-line',
      color: 'bg-[#FFC72C]'
    },
    {
      title: 'Negotiation Mastery',
      description: 'Learn proven strategies to negotiate salary, promotions, and business deals effectively.',
      icon: 'ri-hand-coin-line',
      color: 'bg-[#1E90FF]'
    },
    {
      title: 'Strategic Networking',
      description: 'Build meaningful professional relationships that accelerate your career growth.',
      icon: 'ri-links-line',
      color: 'bg-[#E6007E]'
    },
    {
      title: 'Financial Literacy',
      description: 'Master personal finance, investing, and wealth-building strategies for long-term success.',
      icon: 'ri-money-dollar-circle-line',
      color: 'bg-[#FFC72C]'
    },
    {
      title: 'Giving and Receiving Feedback',
      description: 'Master the art of delivering constructive feedback and receiving criticism with grace to foster growth and stronger relationships.',
      icon: 'ri-chat-check-line',
      color: 'bg-[#1E90FF]'
    },
    {
      title: 'Strategic Delegation',
      description: 'Learn how to effectively delegate tasks, empower your team, and maximize productivity while maintaining quality.',
      icon: 'ri-team-line',
      color: 'bg-[#E6007E]'
    },
    {
      title: 'Personal Branding',
      description: 'Build a powerful personal brand that showcases your unique value and opens doors to new opportunities.',
      icon: 'ri-user-heart-line',
      color: 'bg-[#FFC72C]'
    },
    {
      title: 'Time Management',
      description: 'Master prioritization, eliminate distractions, and optimize your schedule to achieve more while maintaining work-life balance.',
      icon: 'ri-time-line',
      color: 'bg-[#1E90FF]'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-[#1E90FF] to-[#0066cc]">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/20"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="font-['Poppins'] font-bold text-6xl md:text-7xl lg:text-8xl text-white mb-6">
              MAS Academy
            </h1>
            <p className="font-['Inter'] text-xl md:text-2xl text-white/95 mb-8">
              Professional development workshops designed specifically for Latina leaders
            </p>
            <p className="font-['Inter'] text-lg text-white/90 max-w-2xl mx-auto">
              Self-development is essential for every career. Financial barriers should never prevent Latinas from accessing the opportunities they deserve.
            </p>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-['Poppins'] font-bold text-4xl md:text-5xl text-[#111111] mb-8">
              Breaking Down Barriers
            </h2>
            <p className="font-['Inter'] text-xl text-gray-700 leading-relaxed mb-6">
              Professional development workshops can cost thousands of dollars, creating an insurmountable barrier for many talented Latinas. MAS Academy eliminates this obstacle by providing world-class training at no cost.
            </p>
            <p className="font-['Inter'] text-xl text-gray-700 leading-relaxed">
              Thanks to our generous donors and corporate sponsors, we offer comprehensive workshops taught by industry experts who are committed to elevating Latina leadership.
            </p>
          </div>
        </div>
      </section>

      {/* Courses Grid */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <h2 className="font-['Poppins'] font-bold text-4xl md:text-5xl text-[#111111] mb-4 text-center">
            Our Workshops
          </h2>
          <p className="font-['Inter'] text-xl text-gray-600 mb-16 text-center">
            All workshops are 100% free for MasDeUna members
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {courses.map((course, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 flex flex-col"
              >
                <div className={`w-16 h-16 ${course.color} rounded-xl flex items-center justify-center mb-6`}>
                  <i className={`${course.icon} text-3xl text-white`}></i>
                </div>
                
                <h3 className="font-['Poppins'] font-bold text-2xl text-[#111111] mb-4">
                  {course.title}
                </h3>
                
                <p className="font-['Inter'] text-gray-700 mb-6 leading-relaxed flex-grow">
                  {course.description}
                </p>
                
                <Link
                  to="/#contact"
                  className="block w-full py-3 bg-[#111111] text-white font-['Poppins'] font-semibold rounded-lg hover:bg-[#E6007E] transition-all duration-300 whitespace-nowrap cursor-pointer text-center mt-auto"
                >
                  Learn More
                </Link>
              </div>
            ))}
          </div>

          {/* New Course Ideas Call-out */}
          <div className="max-w-4xl mx-auto mt-16 bg-gradient-to-br from-[#E6007E]/10 to-[#FFC72C]/10 rounded-2xl p-10 border-2 border-[#E6007E]/20">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#E6007E] rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-lightbulb-line text-3xl text-white"></i>
              </div>
              <h3 className="font-['Poppins'] font-bold text-3xl text-[#111111] mb-4">
                Have a Workshop Idea?
              </h3>
              <p className="font-['Inter'] text-lg text-gray-700 mb-8 leading-relaxed">
                We're always looking to expand our curriculum based on the needs of trends in the workplace. If you have an idea for a new workshop or topic you'd like to see covered, if you'd like to teach a workshop we want to hear from you!
              </p>
              <Link
                to="/#contact"
                className="inline-block px-10 py-4 bg-[#E6007E] text-white font-['Poppins'] font-bold text-lg rounded-lg hover:bg-[#c00069] transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                Share Your Ideas
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <h2 className="font-['Poppins'] font-bold text-4xl md:text-5xl text-[#111111] mb-16 text-center">
            How It Works
          </h2>

          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-20 h-20 bg-[#E6007E] rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-4xl font-bold text-white">1</span>
                </div>
                <h3 className="font-['Poppins'] font-bold text-2xl text-[#111111] mb-4">
                  Apply
                </h3>
                <p className="font-['Inter'] text-lg text-gray-700">
                  Complete a simple application to join MAS Academy. All MasDeUna members are eligible.
                </p>
              </div>

              <div className="text-center">
                <div className="w-20 h-20 bg-[#FFC72C] rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-4xl font-bold text-[#111111]">2</span>
                </div>
                <h3 className="font-['Poppins'] font-bold text-2xl text-[#111111] mb-4">
                  Learn
                </h3>
                <p className="font-['Inter'] text-lg text-gray-700">
                  Attend live virtual sessions, complete assignments, and engage with fellow Latinas.
                </p>
              </div>

              <div className="text-center">
                <div className="w-20 h-20 bg-[#1E90FF] rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-4xl font-bold text-white">3</span>
                </div>
                <h3 className="font-['Poppins'] font-bold text-2xl text-[#111111] mb-4">
                  Grow
                </h3>
                <p className="font-['Inter'] text-lg text-gray-700">
                  Apply your new skills, advance your career, and mentor the next generation.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-[#111111] text-white">
        <div className="container mx-auto px-6">
          <h2 className="font-['Poppins'] font-bold text-4xl md:text-5xl mb-16 text-center">
            Success Stories
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8">
              <div className="flex items-center gap-2 mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-[#FFC72C] text-xl"></i>
                ))}
              </div>
              <p className="font-['Inter'] text-lg text-white/90 mb-6 leading-relaxed italic">
                "The Leadership Fundamentals workshop gave me the confidence to apply for a director position. I got the job and a 40% salary increase!"
              </p>
              <p className="font-['Poppins'] font-semibold text-[#FFC72C]">
                Marketing Director
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8">
              <div className="flex items-center gap-2 mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-[#FFC72C] text-xl"></i>
                ))}
              </div>
              <p className="font-['Inter'] text-lg text-white/90 mb-6 leading-relaxed italic">
                "Negotiation Mastery changed my life. I negotiated my first six-figure salary and learned to advocate for myself without fear."
              </p>
              <p className="font-['Poppins'] font-semibold text-[#FFC72C]">
                Senior Product Manager
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8">
              <div className="flex items-center gap-2 mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-[#FFC72C] text-xl"></i>
                ))}
              </div>
              <p className="font-['Inter'] text-lg text-white/90 mb-6 leading-relaxed italic">
                "The Financial Literacy workshop helped me pay off debt and start investing. I'm building generational wealth for my family."
              </p>
              <p className="font-['Poppins'] font-semibold text-[#FFC72C]">
                Operations Manager
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8">
              <div className="flex items-center gap-2 mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-[#FFC72C] text-xl"></i>
                ))}
              </div>
              <p className="font-['Inter'] text-lg text-white/90 mb-6 leading-relaxed italic">
                "Personal Branding helped me establish myself as a thought leader. I'm now speaking at conferences and attracting amazing opportunities."
              </p>
              <p className="font-['Poppins'] font-semibold text-[#FFC72C]">
                VP of Strategy
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#E6007E] to-[#c00069]">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-['Poppins'] font-bold text-4xl md:text-5xl text-white mb-8">
              Ready to Invest in Yourself?
            </h2>
            <p className="font-['Inter'] text-xl text-white/95 mb-12">
              Join MAS Academy and access world-class professional development at no cost.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Link
                to="/join"
                className="inline-block px-10 py-5 bg-white text-[#E6007E] font-['Poppins'] font-bold text-lg rounded-lg hover:bg-gray-100 transition-all duration-300 shadow-xl hover:shadow-2xl whitespace-nowrap cursor-pointer"
              >
                Apply Now
              </Link>
              <Link
                to="/donate"
                className="inline-block px-10 py-5 bg-[#111111] text-white font-['Poppins'] font-bold text-lg rounded-lg hover:bg-black transition-all duration-300 shadow-xl hover:shadow-2xl whitespace-nowrap cursor-pointer"
              >
                Sponsor a Workshop
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
